import { HttpClient } from '@angular/common/http';
import { inject, Injectable, signal } from '@angular/core';
import { lastValueFrom, take } from 'rxjs';
import { environment } from '../../../../environments/environment';

export interface ParentPatrolPlatformFlags {
  discord: boolean;
  roblox: boolean;
}

@Injectable({ providedIn: 'root' })
export class ParentPatrolPlatformFlagsService {
  #http = inject(HttpClient);
  #flags = signal<ParentPatrolPlatformFlags>({ discord: false, roblox: false });
  #ready = signal(false);
  #loadPromise: Promise<void> | null = null;

  readonly flags = this.#flags.asReadonly();
  readonly ready = this.#ready.asReadonly();

  isAnyEnabled(): boolean {
    const flags = this.#flags();
    return flags.discord || flags.roblox;
  }

  isDiscordEnabled(): boolean {
    return this.#flags().discord;
  }

  isRobloxEnabled(): boolean {
    return this.#flags().roblox;
  }

  async whenReady(): Promise<void> {
    if (this.#ready()) {
      return;
    }
    if (!this.#loadPromise) {
      this.#loadPromise = this.#load();
    }
    await this.#loadPromise;
  }

  async reload(): Promise<void> {
    this.#ready.set(false);
    this.#loadPromise = this.#load();
    await this.#loadPromise;
  }

  async #load(): Promise<void> {
    try {
      const flags = await lastValueFrom(
        this.#http
          .get<ParentPatrolPlatformFlags>(
            `${environment.baseUrl}/user/patrol/platform-flags`,
          )
          .pipe(take(1)),
      );
      this.#flags.set(flags);
    } catch {
      this.#flags.set({ discord: false, roblox: false });
    } finally {
      this.#ready.set(true);
    }
  }
}
